import React, { useState, useEffect, useMemo } from "react";
import AdminLayout from "../../components/AdminLayout";
import Modal from "../../components/Modal";
import api, { baseURL } from "../../services/api";
import { toast } from "react-toastify";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiSearch,
  FiCalendar,
  FiPaperclip,
  FiFile,
  FiImage,
} from "react-icons/fi";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const CommunicationManagement = () => {
  const [communications, setCommunications] = useState([]);
  const [lifeSavers, setLifeSavers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editingCommunication, setEditingCommunication] = useState(null);
  const [viewingCommunication, setViewingCommunication] = useState(null);

  // Search filters
  const [searchTitle, setSearchTitle] = useState("");
  const [searchMessage, setSearchMessage] = useState("");
  const [searchMonth, setSearchMonth] = useState("");
  const [searchYear, setSearchYear] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    message: "",
    message_date: "",
    visibility_type: "all",
    visible_to: [],
  });
  const [voiceFile, setVoiceFile] = useState(null);
  const [documentFile, setDocumentFile] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [commRes, lifeSaversRes] = await Promise.all([
        api.get("/communications/admin"),
        api.get("/life-savers"),
      ]);
      setCommunications(commRes.data);
      setLifeSavers(lifeSaversRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (communication = null) => {
    if (communication) {
      setEditingCommunication(communication);
      setFormData({
        title: communication.title || "",
        message: communication.message || "",
        message_date: communication.message_date
          ? new Date(communication.message_date)
          : new Date(),
        visibility_type: communication.visibility_type || "all",
        visible_to: communication.visible_to?.map((v) => v.life_saver_id) || [],
      });
    } else {
      setEditingCommunication(null);
      setFormData({
        title: "",
        message: "",
        message_date: new Date(),
        visibility_type: "all",
        visible_to: [],
      });
    }
    setVoiceFile(null);
    setDocumentFile(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingCommunication(null);
  };

  const handleView = async (id) => {
    try {
      const response = await api.get(`/communications/${id}`);
      setViewingCommunication(response.data);
      setViewModalOpen(true);
    } catch (error) {
      toast.error("Failed to fetch communication details");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleVisibilityChange = (lifeSaverId) => {
    setFormData((prev) => {
      const visible_to = prev.visible_to.includes(lifeSaverId)
        ? prev.visible_to.filter((id) => id !== lifeSaverId)
        : [...prev.visible_to, lifeSaverId];
      return { ...prev, visible_to };
    });
  };

  const handleFileChange = (e) => {
    setVoiceFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = new FormData();
      data.append("title", formData.title);
      data.append("message", formData.message);
      // Format date to ISO string for API
      const formattedDate =
        formData.message_date instanceof Date
          ? formData.message_date.toISOString().split("T")[0]
          : formData.message_date;
      data.append("message_date", formattedDate);
      data.append("visibility_type", formData.visibility_type);

      if (formData.visibility_type === "specific") {
        formData.visible_to.forEach((id) => {
          data.append("visible_to[]", id);
        });
      }

      if (voiceFile) {
        data.append("voice_file", voiceFile);
      }

      if (documentFile) {
        data.append("document_file", documentFile);
      }

      if (editingCommunication) {
        await api.put(`/communications/${editingCommunication.id}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Communication updated successfully");
      } else {
        await api.post("/communications", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Communication created successfully");
      }
      handleCloseModal();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this communication?")) {
      try {
        await api.delete(`/communications/${id}`);
        toast.success("Communication deleted successfully");
        fetchData();
      } catch (error) {
        toast.error("Failed to delete communication");
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get unique years from communications for year filter dropdown
  const availableYears = useMemo(() => {
    const years = communications.map((comm) =>
      new Date(comm.message_date).getFullYear()
    );
    return [...new Set(years)].sort((a, b) => b - a);
  }, [communications]);

  // Filtered communications based on search criteria
  const filteredCommunications = useMemo(() => {
    return communications.filter((comm) => {
      const commDate = new Date(comm.message_date);
      const commMonth = commDate.getMonth() + 1;
      const commYear = commDate.getFullYear();

      // Title filter
      if (
        searchTitle &&
        !(comm.title || "").toLowerCase().includes(searchTitle.toLowerCase())
      ) {
        return false;
      }

      // Message filter
      if (
        searchMessage &&
        !(comm.message || "")
          .toLowerCase()
          .includes(searchMessage.toLowerCase())
      ) {
        return false;
      }

      // Month filter
      if (searchMonth && commMonth !== parseInt(searchMonth)) {
        return false;
      }

      // Year filter
      if (searchYear && commYear !== parseInt(searchYear)) {
        return false;
      }

      return true;
    });
  }, [communications, searchTitle, searchMessage, searchMonth, searchYear]);

  const clearFilters = () => {
    setSearchTitle("");
    setSearchMessage("");
    setSearchMonth("");
    setSearchYear("");
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="spinner"></div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="page-header">
        <h1 className="page-title">Communication Management</h1>
        <button className="btn btn-primary" onClick={() => handleOpenModal()}>
          <FiPlus />
          Add Communication
        </button>
      </div>

      {/* Search Filters */}
      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <div className="search-filters">
          <h3
            style={{
              marginBottom: "1rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <FiSearch /> Search & Filter
          </h3>
          <div
            className="filter-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "1rem",
            }}
          >
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Title</label>
              <input
                type="text"
                placeholder="Search by title..."
                value={searchTitle}
                onChange={(e) => setSearchTitle(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Message</label>
              <input
                type="text"
                placeholder="Search by message..."
                value={searchMessage}
                onChange={(e) => setSearchMessage(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Month</label>
              <select
                value={searchMonth}
                onChange={(e) => setSearchMonth(e.target.value)}
              >
                <option value="">All Months</option>
                <option value="1">January</option>
                <option value="2">February</option>
                <option value="3">March</option>
                <option value="4">April</option>
                <option value="5">May</option>
                <option value="6">June</option>
                <option value="7">July</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Year</label>
              <select
                value={searchYear}
                onChange={(e) => setSearchYear(e.target.value)}
              >
                <option value="">All Years</option>
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div
            style={{
              marginTop: "1rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span style={{ color: "#666", fontSize: "0.9rem" }}>
              Showing {filteredCommunications.length} of {communications.length}{" "}
              communications
            </span>
            {(searchTitle || searchMessage || searchMonth || searchYear) && (
              <button
                className="btn btn-secondary"
                onClick={clearFilters}
                style={{ padding: "0.5rem 1rem" }}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card overflow-x-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Title</th>
              <th>Voice</th>
              <th>Visibility</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCommunications.map((comm) => (
              <tr key={comm.id}>
                <td>{formatDate(comm.message_date)}</td>
                <td>{comm.title || "No title"}</td>
                <td>
                  {comm.voice_file_path ? (
                    <span className="badge badge-success">Has Voice</span>
                  ) : (
                    <span className="badge badge-warning">No Voice</span>
                  )}
                </td>
                <td>
                  <span
                    className={`badge ${
                      comm.visibility_type === "all"
                        ? "badge-info"
                        : "badge-warning"
                    }`}
                  >
                    {comm.visibility_type === "all"
                      ? "All"
                      : `${comm.visible_to_count} selected`}
                  </span>
                </td>
                <td>
                  <button
                    className="action-btn view"
                    onClick={() => handleView(comm.id)}
                    title="View"
                  >
                    <FiEye />
                  </button>
                  <button
                    className="action-btn edit"
                    onClick={() => handleOpenModal(comm)}
                    title="Edit"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    className="action-btn delete"
                    onClick={() => handleDelete(comm.id)}
                    title="Delete"
                  >
                    <FiTrash2 />
                  </button>
                </td>
              </tr>
            ))}
            {filteredCommunications.length === 0 && (
              <tr>
                <td colSpan="5" className="text-center">
                  {communications.length === 0
                    ? "No communications found"
                    : "No communications match your search criteria"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={
          editingCommunication ? "Edit Communication" : "Add Communication"
        }
      >
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-section">
            <div className="form-section-header">
              <span>Communication Details</span>
            </div>

            <div className="form-row form-row-2col">
              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  name="title"
                  className="form-input"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter title (optional)"
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  Message Date <span className="required">*</span>
                </label>
                <div className="datepicker-wrapper">
                  <FiCalendar className="datepicker-icon" />
                  <DatePicker
                    selected={formData.message_date}
                    onChange={(date) =>
                      setFormData((prev) => ({ ...prev, message_date: date }))
                    }
                    dateFormat="MMMM d, yyyy"
                    className="form-input datepicker-input"
                    placeholderText="Select a date"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    todayButton="Today"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Message</label>
                <textarea
                  name="message"
                  className="form-textarea"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Enter your message content..."
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Voice Message (Optional)</label>
                <div className="file-upload-wrapper">
                  <input
                    type="file"
                    accept="audio/*"
                    onChange={handleFileChange}
                    className="file-input"
                    id="voice-file-input"
                  />
                  <label
                    htmlFor="voice-file-input"
                    className="file-upload-label"
                  >
                    <span className="file-upload-icon">🎤</span>
                    <span className="file-upload-text">
                      {voiceFile ? voiceFile.name : "Choose audio file"}
                    </span>
                  </label>
                </div>
                {editingCommunication?.voice_file_path && (
                  <div className="current-file-info">
                    <span className="current-file-label">Current file:</span>
                    <span className="current-file-name">
                      {editingCommunication.voice_file_path.split("/").pop()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  {/* <FiPaperclip style={{ marginRight: "0.25rem" }} /> */}
                  Document/Image (Optional)
                </label>
                <div className="file-upload-wrapper">
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setDocumentFile(e.target.files[0])}
                    className="file-input"
                    id="document-file-input"
                  />
                  <label
                    htmlFor="document-file-input"
                    className="file-upload-label"
                  >
                    <span className="file-upload-icon">📄</span>
                    <span className="file-upload-text">
                      {documentFile
                        ? documentFile.name
                        : "Choose image or PDF file"}
                    </span>
                  </label>
                </div>
                <small
                  style={{
                    color: "#888",
                    display: "block",
                    marginTop: "0.25rem",
                  }}
                >
                  Supported: Images (JPEG, PNG, GIF, WebP) and PDF files (max
                  10MB)
                </small>
                {editingCommunication?.document_file_path && (
                  <div className="current-file-info">
                    <span className="current-file-label">
                      Current document:
                    </span>
                    <span className="current-file-name">
                      {editingCommunication.document_file_path.split("/").pop()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-section-header">
              <span>Visibility Settings</span>
            </div>

            <div className="visibility-card">
              <div className="visibility-options-grid">
                <label
                  className={`visibility-option-card ${
                    formData.visibility_type === "all" ? "selected" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="visibility_type"
                    value="all"
                    checked={formData.visibility_type === "all"}
                    onChange={handleInputChange}
                  />
                  <div className="visibility-option-content">
                    <span className="visibility-option-title">All Members</span>
                    <span className="visibility-option-desc">
                      Visible to everyone
                    </span>
                  </div>
                </label>
                <label
                  className={`visibility-option-card ${
                    formData.visibility_type === "specific" ? "selected" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="visibility_type"
                    value="specific"
                    checked={formData.visibility_type === "specific"}
                    onChange={handleInputChange}
                  />
                  <div className="visibility-option-content">
                    <span className="visibility-option-title">
                      Specific Members
                    </span>
                    <span className="visibility-option-desc">
                      Choose who can view
                    </span>
                  </div>
                </label>
              </div>

              {formData.visibility_type === "specific" && (
                <div className="lifesaver-selection">
                  <div className="lifesaver-list-modern">
                    {lifeSavers.map((ls) => (
                      <label
                        key={ls.id}
                        className={`lifesaver-item-modern ${
                          formData.visible_to.includes(ls.id) ? "selected" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.visible_to.includes(ls.id)}
                          onChange={() => handleVisibilityChange(ls.id)}
                        />
                        <div className="lifesaver-item-info">
                          <span className="lifesaver-name">{ls.full_name}</span>
                          <span className="lifesaver-email">{ls.email}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer-modern">
            <button
              type="button"
              className="btn btn-secondary-modern"
              onClick={handleCloseModal}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary-modern">
              {editingCommunication
                ? "Update Communication"
                : "Create Communication"}
            </button>
          </div>
        </form>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        title="Communication Details"
      >
        {viewingCommunication && (
          <div>
            <div className="form-group">
              <label>
                <strong>Date:</strong>
              </label>
              <p>{formatDate(viewingCommunication.message_date)}</p>
            </div>

            <div className="form-group">
              <label>
                <strong>Title:</strong>
              </label>
              <p>{viewingCommunication.title || "No title"}</p>
            </div>

            <div className="form-group">
              <label>
                <strong>Message:</strong>
              </label>
              <p style={{ whiteSpace: "pre-wrap" }}>
                {viewingCommunication.message || "No message"}
              </p>
            </div>

            {viewingCommunication.voice_file_path && (
              <div className="form-group voice-player">
                <label>
                  <strong>Voice Message:</strong>
                </label>
                <audio controls style={{ width: "100%", marginTop: "0.5rem" }}>
                  <source
                    src={`${baseURL}${viewingCommunication.voice_file_path}`}
                  />
                  Your browser does not support the audio element.
                </audio>
              </div>
            )}

            {viewingCommunication.document_file_path && (
              <div className="form-group">
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <FiPaperclip />
                  <strong>Attached Document:</strong>
                </label>
                <div style={{ marginTop: "0.5rem" }}>
                  {viewingCommunication.document_file_path.match(
                    /\.(jpg|jpeg|png|gif|webp)$/i
                  ) ? (
                    <div>
                      <img
                        src={`${baseURL}${viewingCommunication.document_file_path}`}
                        alt="Attached document"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "300px",
                          borderRadius: "8px",
                          border: "1px solid #ddd",
                        }}
                      />
                      <div style={{ marginTop: "0.5rem" }}>
                        <a
                          href={`${baseURL}${viewingCommunication.document_file_path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-secondary"
                          style={{
                            fontSize: "0.85rem",
                            padding: "0.4rem 0.8rem",
                          }}
                        >
                          <FiImage style={{ marginRight: "0.25rem" }} />
                          View Full Size
                        </a>
                      </div>
                    </div>
                  ) : (
                    <a
                      href={`${baseURL}${viewingCommunication.document_file_path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <FiFile />
                      View/Download PDF
                    </a>
                  )}
                </div>
              </div>
            )}

            <div className="form-group">
              <label>
                <strong>Visibility:</strong>
              </label>
              <p>
                {viewingCommunication.visibility_type === "all"
                  ? "All Members"
                  : `${
                      viewingCommunication.visible_to?.length || 0
                    } specific Members`}
              </p>
              {viewingCommunication.visibility_type === "specific" &&
                viewingCommunication.visible_to?.length > 0 && (
                  <ul>
                    {viewingCommunication.visible_to.map((v, idx) => (
                      <li key={idx}>{v.full_name}</li>
                    ))}
                  </ul>
                )}
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setViewModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
};

export default CommunicationManagement;
